import { useState } from 'react';
import { Plus, Power } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { PessoasModal } from '../components/PessoasModal';
import { useMotivosReintegracao, useSalvarMotivoReintegracao } from '../hooks/desligamento';

/** Motivos de reintegração parametrizáveis (US-GP-010). */
export default function MotivosReintegracaoPage() {
  const toast = useToast();
  const motivos = useMotivosReintegracao(true);
  const salvar = useSalvarMotivoReintegracao();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', requiresDocument: false, requiresLegalApproval: false });
  const [error, setError] = useState<string | null>(null);

  async function criar() {
    try {
      await salvar.mutateAsync({ id: null, payload: form });
      setOpen(false);
      toast.success('Motivo criado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível criar o motivo.'));
    }
  }

  async function alternar(id: string, active: boolean) {
    try {
      await salvar.mutateAsync({ id, payload: { active: !active } });
      toast.success(active ? 'Motivo inativado.' : 'Motivo reativado.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar o motivo.'));
    }
  }

  return (
    <PageContainer title="Motivos de reintegração" breadcrumbs={[{ label: 'Pessoas' }, { label: 'Motivos de reintegração' }]}
      action={
        <Can feature={FEATURES.pessoasReintegracoes} action="create">
          <Button onClick={() => { setForm({ code: '', name: '', requiresDocument: false, requiresLegalApproval: false }); setError(null); setOpen(true); }}>
            <Plus className="mr-2 h-5 w-5" /> Novo motivo
          </Button>
        </Can>
      }>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
        {(motivos.data ?? []).map((m) => (
          <li key={m.id} className={`flex items-center justify-between px-4 py-3 text-sm ${m.active ? '' : 'opacity-60'}`}>
            <span>
              <span className="font-medium text-gray-900">{m.name}</span> <span className="font-mono text-xs text-gray-500">{m.code}</span>
              <span className="block text-xs text-gray-500">
                {[m.requiresDocument && 'exige documento', m.requiresLegalApproval && 'exige aprovação jurídica'].filter(Boolean).join(' · ') || 'sem exigências extras'}
              </span>
            </span>
            <Can feature={FEATURES.pessoasReintegracoes} action="edit">
              <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => alternar(m.id, m.active)}
                aria-label={m.active ? `Inativar ${m.name}` : `Reativar ${m.name}`}><Power className="h-4 w-4" /></button>
            </Can>
          </li>
        ))}
      </ul>
      <PessoasModal open={open} title="Novo motivo" submitLabel="Criar" isSubmitting={salvar.isPending} submitError={error}
        onClose={() => setOpen(false)} onSubmit={criar}>
        <TextInput label="Código" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))} />
        <TextInput label="Nome" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requiresDocument}
          onChange={(e) => setForm((f) => ({ ...f, requiresDocument: e.target.checked }))} /> Exige documento</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requiresLegalApproval}
          onChange={(e) => setForm((f) => ({ ...f, requiresLegalApproval: e.target.checked }))} /> Exige aprovação jurídica</label>
      </PessoasModal>
    </PageContainer>
  );
}
